import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { renderHtml } from '@/lib/document-utils';
import { signImageUrlsInHtml } from '@/lib/utils';

const NEEDS_SIGNING_RE = /src=["'][^"']*\/outputs\/extracted_images\//i;

/**
 * Strip unsigned image src attributes so the browser never requests the
 * canonical /outputs/extracted_images/ paths directly (nginx returns 403).
 * Keeps the <img> tags intact with a transparent placeholder.
 */
function stripUnsignedImages(html: string): string {
  return html.replace(
    /(<img\s+[^>]*?)src=["']\/outputs\/extracted_images\/[^"']*["']/gi,
    '$1src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"'
  );
}

interface SignedHtmlProps {
  html: string;
  className?: string;
}

/**
 * SignedHtml component that handles async signing of image URLs in HTML content.
 * Renders immediately with placeholder images, then swaps in signed URLs.
 */
const SignedHtml: React.FC<SignedHtmlProps> = memo(({ html, className = '' }) => {
  const [signed, setSigned] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = await signImageUrlsInHtml(html);
      if (!cancelled) setSigned(out);
    })();
    return () => { cancelled = true; };
  }, [html]);

  const handleImageErrors = useCallback(() => {
    if (!containerRef.current) return;

    const images = containerRef.current.querySelectorAll('img');
    images.forEach((img) => {
      if (img.dataset.errorHandled) return;
      img.dataset.errorHandled = 'true';

      img.onerror = () => {
        img.classList.add('image-load-error');
        if (!img.alt || img.alt === '') {
          img.alt = 'Image non disponible';
        }
        img.dataset.failed = 'true';
      };

      if (img.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
        img.classList.add('image-load-error');
        if (!img.alt || img.alt === '') {
          img.alt = 'Image non disponible';
        }
        img.dataset.failed = 'true';
      }
    });
  }, []);

  useEffect(() => {
    handleImageErrors();
  }, [signed, handleImageErrors]);

  const baseClassName = 'prose prose-sm max-w-none document-content';
  const finalClassName = `${baseClassName} ${className}`.trim();

  if (!signed) {
    // Render with stripped images so the browser doesn't request unsigned URLs
    const safeHtml = NEEDS_SIGNING_RE.test(html) ? stripUnsignedImages(html) : html;
    return <div ref={containerRef} className={finalClassName} dangerouslySetInnerHTML={renderHtml(safeHtml)} />;
  }

  return <div ref={containerRef} className={finalClassName} dangerouslySetInnerHTML={renderHtml(signed)} />;
});

SignedHtml.displayName = 'SignedHtml';

export default SignedHtml;
